// lib/wei.js
//
// BigInt-safe wei <-> display helpers. CLAUDE.md non-negotiable: every
// money amount on the wire is a uint256 string; we use BigInt for math and
// only convert to display strings at the leaf. NEVER Number() on a wei
// string (loses precision past 2^53).
//
// kStables in the Bankcoin family use 6 decimals (mirror of USDC/EURC).
// The `decimals` arg defaults to 6 — passing it explicitly is preferred.

export const DEFAULT_KSTABLE_DECIMALS = 6;

/**
 * Parse a user-typed decimal string (e.g. "50.00" or "0.000001") into a
 * BigInt wei value at the given decimals. Rejects malformed input by
 * throwing.
 *
 * Examples:
 *   parseUnits("50", 6)        -> 50000000n
 *   parseUnits("0.5", 6)       -> 500000n
 *   parseUnits("0.000001", 6)  -> 1n
 *   parseUnits("", 6)          -> 0n  (empty == zero, NOT a parse error)
 */
export function parseUnits(input, decimals = DEFAULT_KSTABLE_DECIMALS) {
  if (input === null || input === undefined) return 0n;
  const s = String(input).trim();
  if (s === '') return 0n;
  if (!/^\d*(\.\d*)?$/.test(s)) {
    throw new Error(`invalid decimal: ${input}`);
  }
  const [whole = '0', frac = ''] = s.split('.');
  if (frac.length > decimals) {
    throw new Error(`too many decimals (max ${decimals})`);
  }
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  // Use the unsigned-cast trick: BigInt("0XXX") is fine.
  const big = BigInt(whole || '0') * 10n ** BigInt(decimals) + BigInt(padded || '0');
  return big;
}

/**
 * Format a BigInt or BigInt-string wei value into a human-readable
 * decimal string at the given decimals.
 *
 *   formatUnits(50000000n, 6)  -> "50"
 *   formatUnits(50125000n, 6)  -> "50.125"
 *   formatUnits(0n, 6)         -> "0"
 *
 * Trims trailing zeros and the dot.
 */
export function formatUnits(value, decimals = DEFAULT_KSTABLE_DECIMALS) {
  const v = typeof value === 'bigint' ? value : BigInt(value ?? 0);
  const negative = v < 0n;
  const absVal = negative ? -v : v;
  const divisor = 10n ** BigInt(decimals);
  const whole = absVal / divisor;
  const frac = absVal % divisor;
  let fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
  const out = fracStr.length === 0 ? whole.toString() : `${whole}.${fracStr}`;
  return negative ? `-${out}` : out;
}

/**
 * Compute the bridge fee in wei.
 *   amountWei * feeBps / 10000  (integer division — fee rounds down,
 *   so the customer is never under-billed on fractional weis).
 */
export function computeFeeWei(amountWei, feeBps) {
  const amt = typeof amountWei === 'bigint' ? amountWei : BigInt(amountWei ?? 0);
  const bps = typeof feeBps === 'bigint' ? feeBps : BigInt(feeBps ?? 0);
  if (amt < 0n) throw new Error('amount must be non-negative');
  if (bps < 0n) throw new Error('feeBps must be non-negative');
  return (amt * bps) / 10_000n;
}

/**
 * Estimated kStable minted on the destination chain:
 *   amount - fee  (clamped at 0 for safety)
 */
export function computeNetWei(amountWei, feeBps) {
  const amt = typeof amountWei === 'bigint' ? amountWei : BigInt(amountWei ?? 0);
  const fee = computeFeeWei(amt, feeBps);
  const net = amt - fee;
  return net < 0n ? 0n : net;
}

/**
 * Full bridge fee = protocolFeeBps × amount / 10000 (floor) + the flat
 * gasFeeRawWei recovery fee. MUST mirror BridgeSenderUpgradeable.requestSwap's
 * on-chain formula exactly:
 *
 *   protocolFeeWei = amount * bps / 10_000   (Solidity integer division)
 *   feeWei         = protocolFeeWei + gasFeeWei
 *
 * computeFeeWei() above only covers the bps component — using it alone for
 * the bridge summary would UNDER-count the fee (the contract also deducts
 * gasFeeForChain[dest]), meaning a minOutAmount built from that estimate
 * would exceed the real on-chain net and every requestSwap would revert with
 * InsufficientNetAfterFees. Always use this pair for bridge fee display / the
 * minOut computed for calldata.
 */
export function computeBridgeFeeWei(amountWei, feeBps, gasFeeRawWei = 0n) {
  const amt = typeof amountWei === 'bigint' ? amountWei : BigInt(amountWei ?? 0);
  const bps = typeof feeBps === 'bigint' ? feeBps : BigInt(feeBps ?? 0);
  const gas = typeof gasFeeRawWei === 'bigint' ? gasFeeRawWei : BigInt(gasFeeRawWei ?? 0);
  if (amt < 0n) throw new Error('amount must be non-negative');
  if (bps < 0n) throw new Error('feeBps must be non-negative');
  if (gas < 0n) throw new Error('gasFeeRawWei must be non-negative');
  const protocolFee = (amt * bps) / 10_000n;
  return protocolFee + gas;
}

/**
 * Net amount the destination chain will mint = amount - (bps fee + flat gas
 * fee), clamped at 0. Matches BridgeSenderUpgradeable.requestSwap's
 * `netAmount` exactly (the contract reverts instead of clamping when
 * feeWei >= amount — callers should treat a 0 result as "amount too small
 * to bridge", not a valid submission).
 */
export function computeBridgeNetWei(amountWei, feeBps, gasFeeRawWei = 0n) {
  const amt = typeof amountWei === 'bigint' ? amountWei : BigInt(amountWei ?? 0);
  const fee = computeBridgeFeeWei(amt, feeBps, gasFeeRawWei);
  const net = amt - fee;
  return net < 0n ? 0n : net;
}

/**
 * Compare two BigInt-ish values safely (handles string + bigint).
 *   cmpWei("1000", 999n) -> 1
 *   cmpWei(0n, 0n)       -> 0
 *   cmpWei(0n, 1n)       -> -1
 */
export function cmpWei(a, b) {
  const av = typeof a === 'bigint' ? a : BigInt(a ?? 0);
  const bv = typeof b === 'bigint' ? b : BigInt(b ?? 0);
  if (av > bv) return 1;
  if (av < bv) return -1;
  return 0;
}
